import React, { useState, useEffect } from "react";
import {
  Tabs,
  Tab,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  CircularProgress,
} from "@mui/material";
import PriceTrend from "./PriceTrend";
import MonthlyAvgPriceGraph from "./MonthlyAvgPriceGraph";
import HeatMap from "./HeatMap";
import Select from "react-select";
import { Auth } from "aws-amplify";
import RiskTrend from "./RiskTrend";
import PriceComparisonChart from "./PriceComparisonChart";

const customStyles = {
  control: (provided) => ({
    ...provided,
  }),
  container: (provided) => ({
    ...provided,
  }),
};

const CompareExplore = (props) => {
  const [tabValue, setTabValue] = useState(0);
  const [sourceOptions, setSourceOptions] = useState("Agmarknet");
  const [pricechartData, setpricechartData] = useState(null); // New state for storing API data
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (props.cropName && props.marketName) {
      props.setPriceSource(null);
      const fetchSourceNames = async () => {
        try {
          const {
            accessToken: { jwtToken },
          } = await Auth.currentSession();
          let requestBody = {
            crop_name: props.cropName,
            market_names: props.marketName,
          };
          const response = await fetch(`/api/explore/getSourceName`, {
            method: "POST",
            headers: {
              Authorization: jwtToken,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
          });
          const data = await response.json();
          const formattedOptions = data.map((source) => ({
            label: source._id,
            value: source._id,
          }));
          setSourceOptions(formattedOptions);
        } catch (error) {
          console.error("Error fetching price sources:", error);
        }
      };

      fetchSourceNames();
    } else {
      setSourceOptions([]);
    }
    const fetchPriceComparisonData = async () => {
      setLoading(true); // Start loading
      try {
        const response = await fetch(`/api/explore/peakHarvestvsStoragePrice?market_name=${props.marketName}`);
        const data = await response.json();
        setpricechartData(data); // Store the response data
      } catch (error) {
        console.error("Error fetching price comparison data:", error);
      } finally {
        setLoading(false); // Stop loading once the data is fetched
      }
    };

    if (props.marketName) {
      fetchPriceComparisonData();
    }

    // eslint-disable-next-line
  }, [props.marketName, props.cropName, props.cropVariety]);


  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if (props.settabSelected) {
      props.settabSelected(newValue);
    }
  };

  const renderPriceTable = () => {
    const years = Array.from(
      new Set(props.monthlyAveragePricesData.map((d) => d.year))
    ).sort((a, b) => a - b);

    if (props.loadingMonthlyAveragePrices) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
          <CircularProgress />
        </Box>
      );
    }

    // Define month name mappings (backend returns short names like 'Jan')
    const monthOrderFull = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const monthOrderShort = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const fullToShort = {
      January: "Jan",
      February: "Feb",
      March: "Mar",
      April: "Apr",
      May: "May",
      June: "Jun",
      July: "Jul",
      August: "Aug",
      September: "Sep",
      October: "Oct",
      November: "Nov",
      December: "Dec",
    };
    const shortToFull = Object.fromEntries(
      Object.entries(fullToShort).map(([f, s]) => [s, f])
    );

    const startMonthFull = props.monitoringStartMonth || "January";
    const endMonthFull = props.monitoringEndMonth || "December";
    const startIdx = monthOrderFull.indexOf(startMonthFull);
    const endIdx = monthOrderFull.indexOf(endMonthFull);

    let monthsInRangeShort = [];
    if (startIdx === -1 || endIdx === -1) {
      // fallback: use months present in data (keep previous behavior) and sort them
      monthsInRangeShort = Array.from(
        new Set(props.monthlyAveragePricesData.map((d) => d.month))
      ).sort((a, b) => monthOrderShort.indexOf(a) - monthOrderShort.indexOf(b));
    } else if (startIdx <= endIdx) {
      const fullRange = monthOrderFull.slice(startIdx, endIdx + 1);
      monthsInRangeShort = fullRange.map((m) => fullToShort[m]);
    } else {
      // wrap-around (e.g., Nov -> Feb)
      const fullRange = monthOrderFull
        .slice(startIdx)
        .concat(monthOrderFull.slice(0, endIdx + 1));
      monthsInRangeShort = fullRange.map((m) => fullToShort[m]);
    }

    // Organize price data by month and year
    const priceData = {};
    props.monthlyAveragePricesData.forEach(({ month, year, price }) => {
      if (!priceData[month]) priceData[month] = {};
      priceData[month][year] = price;
    });

    return (
      <TableContainer
        component={Paper}
        sx={{ maxHeight: 420, overflowY: "auto" }}
      >
        <Table stickyHeader size="small" sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell
                style={{
                  fontWeight: "bold",
                  border: "1px solid #ddd",
                  padding: "4px",
                }}
              >
                Month
              </TableCell>
              {years.map((year) => (
                <TableCell
                  key={year}
                  style={{
                    fontWeight: "bold",
                    border: "1px solid #ddd",
                    textAlign: "center",
                    padding: "4px",
                  }}
                >
                  {year}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {monthsInRangeShort.map((monthShort) => (
              <TableRow key={monthShort}>
                <TableCell
                  style={{
                    border: "1px solid #ddd",
                    fontWeight: "bold",
                    padding: "5px",
                  }}
                >
                  {shortToFull[monthShort] || monthShort}
                </TableCell>
                {years.map((year) => (
                  <TableCell
                    key={`${monthShort}-${year}`}
                    style={{
                      border: "1px solid #ddd",
                      textAlign: "center",
                      padding: "5px",
                    }}
                  >
                    {priceData[monthShort] && priceData[monthShort][year]
                      ? priceData[monthShort][year]
                      : "-"}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        aria-label="Generate Tabs"
      >
        <Tab
          label="Data Validation"
          sx={{ textTransform: "capitalize", fontSize: "16px" }}
        />
        <Tab
          label="Price Trends"
          sx={{ textTransform: "capitalize", fontSize: "16px" }}
        />
        <Tab
          label="Risk Analytics"
          sx={{ textTransform: "capitalize", fontSize: "16px" }}
        />
      </Tabs>
      <Box sx={{ p: 3 }}>
        {tabValue === 0 && (
          <>
            {props.showCompare ? (
              <Box>
                {props.loading ? (
                  <Typography>Loading...</Typography>
                ) : (
                  <>
                    {props.loadingHeatMap ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', margin: '30px', justifyContent: 'center' }}>
                        <CircularProgress />
                      </Box>
                    ) : props.heatMapData ? (
                      <>
                        <div
                          style={{
                            fontSize: "16px",
                            fontWeight: 500,
                            textAlign: "left",
                            marginBottom: "20px",
                          }}
                        >
                          Percentage share of good data days
                        </div>
                        <HeatMap
                          data={props.heatMapData}
                          variety={props.cropVariety}
                        />
                      </>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', margin: '30px', justifyContent: 'center' }}>
                        <Typography>No data available</Typography>
                      </Box>
                    )}
                    {props.loadingPriceTrend ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                        <CircularProgress />
                      </Box>
                    ) : props.priceTrendData ? (
                      <PriceTrend
                        data={props.priceTrendData}
                        priceSource={props.priceSource}
                        marketName={props.marketName}
                        cropName={props.cropName}
                        startYear={props.policyStartYear}
                        endYear={props.policyEndYear}
                        startMonth={props.monitoringStartMonth}
                        endMonth={props.monitoringEndMonth}
                      />
                    ) : (
                      ""
                    )}
                  </>
                )}
              </Box>
            ) : (
              <div
                style={{
                  display: "flex",
                  position: "absolute",
                  height: "90vh",
                  width: "100%",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundImage: 'url("Agtuall_compare_map.svg")',
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  zIndex: 1,
                }}
              >
                <div className="selectmodule">
                  Fill the Input Fields from the left panel to start exploring
                  Data & Charts
                </div>
              </div>
            )}
          </>
        )}
        {tabValue === 1 && (
          <>
            {props.showCompare ? (
              <div>
                <div style={{ float: "right" }}>
                  <h2 className="select-label">Price Source</h2>
                  <Select
                    className="custom-select"
                    placeholder="Price Source"
                    styles={customStyles}
                    value={
                      props.priceSource
                        ? { label: props.priceSource, value: props.priceSource }
                        : null
                    }
                    onChange={(selectedOption) => {
                      props.setPriceSource(selectedOption.value);
                    }}
                    options={sourceOptions}
                  />
                </div>
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: 500,
                    textAlign: "left",
                    marginBottom: "20px",
                    marginTop: "10px",
                  }}
                >
                  Average Monthly Prices ({props.policyStartYear}-
                  {props.policyEndYear})
                </div>
                {(props.monthlyAvgVsArrivalData != null || props.loadingMonthlyAvgVsArrival) && (
                  <MonthlyAvgPriceGraph data={props.monthlyAvgVsArrivalData} loading={props.loadingMonthlyAvgVsArrival} />
                )}
                {/* Render the compact, scrollable price table */}
                {props.loadingMonthlyAveragePrices ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  props.monthlyAveragePricesData.length > 0 && renderPriceTable()
                )}
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  position: "absolute",
                  height: "90vh",
                  width: "100%",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundImage: 'url("Agtuall_compare_map.svg")',
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  zIndex: 1,
                }}
              >
                <div className="selectmodule">
                  Fill the Input Fields from the left panel to start exploring
                  Data & Charts
                </div>
              </div>
            )}
          </>
        )}
        {tabValue === 2 && (
          <>
            {props.showCompare ? (
              <Box>
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: 500,
                    textAlign: "left",
                    marginBottom: "20px",
                  }}
                >
                  Peak Harvest Prices vs Storage Period Prices
                </div>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <PriceComparisonChart data={pricechartData} marketName={props.marketName} loading={loading} />
                )}
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: 500,
                    textAlign: "left",
                    marginBottom: "20px",
                  }}
                >
                  Supply and Demand trends
                </div>
                <RiskTrend />
              </Box>
            ) : (
              <div
                style={{
                  display: "flex",
                  position: "absolute",
                  height: "90vh",
                  width: "100%",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundImage: 'url("Agtuall_compare_map.svg")',
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  zIndex: 1,
                }}
              >
                <div className="selectmodule">
                  Fill the Input Fields from the left panel to start exploring
                  Data & Charts
                </div>
              </div>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default CompareExplore;
